import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  CustomFieldAppliesTo,
  CustomFieldDefinition,
  CustomFieldDefinitionRequest,
} from 'brain-data/model/custom-field.model';
import { environment } from '../../../../environments/environment';

export const CUSTOM_FIELD_DEFINITIONS_URL = `${environment.apiBaseAdminUrl}/custom-field-definitions`;

/**
 * The tenant's custom-field definitions (AC2 / AC9, RFP 3.2.1.36).
 *
 * The route carries NO version segment — `/api/admin/custom-field-definitions` — because this
 * repository has none anywhere; the brain-payments twin of this surface does carry `/v1`, and the
 * two are not interchangeable. The base is the injected `apiBaseAdminUrl`, so no host, domain or
 * audience appears here (BR-20). The `/api/admin` prefix is the authorization: every route beneath
 * it is gated on ADMIN/SUPERADMIN by the server's own security configuration, and NO `authz`
 * resource is registered for these routes (BR-R21) — registering one without its brain-iam
 * counterpart would floor a route against a permission nobody can hold, which is what the
 * controller's own KDoc records.
 *
 * The list arrives as a BARE ARRAY, which is what the BRAIN Core controller returns; reading the
 * payments `{ data: [...] }` envelope here would yield undefined on every field.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomFieldDefinitionService {
  constructor(private http: HttpClient) {}

  search(appliesTo: CustomFieldAppliesTo): Observable<CustomFieldDefinition[]> {
    return this.http.get<CustomFieldDefinition[]>(CUSTOM_FIELD_DEFINITIONS_URL, {
      params: new HttpParams().set('appliesTo', appliesTo),
    });
  }

  /** `POST` on the collection; the controller answers **201** with the row it stored. */
  create(payload: CustomFieldDefinitionRequest): Observable<CustomFieldDefinition> {
    return this.http.post<CustomFieldDefinition>(CUSTOM_FIELD_DEFINITIONS_URL, payload);
  }

  /** `PUT` on the item route; 200 with the stored row. */
  update(id: number, payload: CustomFieldDefinitionRequest): Observable<CustomFieldDefinition> {
    return this.http.put<CustomFieldDefinition>(`${CUSTOM_FIELD_DEFINITIONS_URL}/${id}`, payload);
  }

  /**
   * `DELETE` on the item route — what AC10 calls RETIRING a definition, because retirement is not
   * modelled: there is no `retired` flag anywhere in this contract. The server answers **204 No
   * Content** on success and refuses with 409 `DEFINITION_HAS_VALUES` while any record still carries
   * a value, so the caller must surface that refusal rather than assume a delete succeeded.
   */
  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${CUSTOM_FIELD_DEFINITIONS_URL}/${id}`);
  }
}
